import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Music, Image, Video, Settings, Plus, Trash2, AlertTriangle, Sparkles, Loader2 } from 'lucide-react';
import { usePersona } from '../hooks/usePersona';
import { useTrackCache } from '../contexts/TrackCacheContext';
import Card, { CardHeader } from '../components/common/Card';
import Button from '../components/common/Button';
import TrackUploadModal from '../components/upload/TrackUploadModal';
import TrackDetailModal from '../components/track/TrackDetailModal';
import VideoCreator from '../components/video/VideoCreator';
import { api } from '../services/api';
import type { TrackContext } from '../types';

// Helper to extract clean track name from filename
function getTrackDisplayName(track: TrackContext): string {
  // Prefer display_name if set
  if (track.display_name) return track.display_name;

  // Otherwise, clean up the filename
  if (track.filename) {
    // Filename format: {timestamp}-{uuid8}-{original_filename}
    // Extract original filename by removing the prefix
    const parts = track.filename.split('-');
    if (parts.length >= 3) {
      // Skip first two parts (timestamp and uuid), rejoin the rest, remove extension
      return parts.slice(2).join('-').replace(/_/g, ' ').replace(/\.[^/.]+$/, '');
    }
    // Fallback: just clean up underscores and remove extension
    return track.filename.replace(/_/g, ' ').replace(/\.[^/.]+$/, '');
  }

  return 'Untitled Track';
}

// Helper to get track status display
function getTrackStatus(track: TrackContext): { label: string; color: string; isAnalyzing: boolean } {
  // Check if analysis is complete
  if (track.analyzed_at || track.processing_status === 'complete') {
    return { label: track.primary_genre || 'Analyzed', color: 'text-zinc-500', isAnalyzing: false };
  }

  // Check if failed
  if (track.processing_status === 'failed') {
    return { label: 'Failed', color: 'text-red-400', isAnalyzing: false };
  }

  // Check if actually processing
  if (track.processing_status === 'processing') {
    return { label: 'Analyzing...', color: 'text-jelly-400', isAnalyzing: true };
  }

  // Pending or no status - not being analyzed
  return { label: 'Not analyzed', color: 'text-zinc-600', isAnalyzing: false };
}

export default function PersonaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { persona, tracks, isLoading, error, refetch } = usePersona(id!);
  const { setTracks } = useTrackCache();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<TrackContext | null>(null);
  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
    const [trackToDelete, setTrackToDelete] = useState<TrackContext | null>(null);
  const [isDeletingTrack, setIsDeletingTrack] = useState(false);
  const [deleteTrackError, setDeleteTrackError] = useState<string | null>(null);

  // VideoCreator state - unified entry point for video creation
  const [videoCreatorTrack, setVideoCreatorTrack] = useState<TrackContext | null>(null);

  // Video projects state
  const [videoProjects, setVideoProjects] = useState<Array<{
    id: string;
    status: string;
    target_platform: string;
    target_duration: number;
    quality_tier: string;
    output_url: string | null;
    actual_cost_usd: number | null;
    created_at: string;
    completed_at: string | null;
  }>>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);

  // Cache tracks when they're loaded
  useEffect(() => {
    if (tracks.length > 0) {
      setTracks(tracks);
    }
  }, [tracks, setTracks]);

  // Fetch video projects for this persona
  useEffect(() => {
    if (!id) return;

    const fetchVideos = async () => {
      setIsLoadingVideos(true);
      try {
        const backendUrl = import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080';
        const response = await fetch(`${backendUrl}/api/persona/${id}/projects`);
        if (response.ok) {
          const data = await response.json();
          setVideoProjects(data.projects || []);
        }
      } catch (err) {
        console.error('Failed to fetch video projects:', err);
      } finally {
        setIsLoadingVideos(false);
      }
    };

    fetchVideos();
  }, [id]);

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    const response = await api.deletePersona(id!);

    if (response.error) {
      setDeleteError(response.error);
      setIsDeleting(false);
      return;
    }

    // Navigate back to personas list
    navigate('/admin/personas');
  };

  const handleGenerateAvatar = async () => {
    setIsGeneratingAvatar(true);
    setAvatarError(null);

    try {
      const backendUrl = import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080';
      const response = await fetch(`${backendUrl}/api/generate-avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona_id: id,
          style: '3d_digital',
          model: 'flux-dev',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error || 'Avatar generation failed');
      }

      // Refetch persona to get updated avatar URL
      refetch();
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to generate avatar');
    } finally {
      setIsGeneratingAvatar(false);
    }
  };

  const handleDeleteTrack = async () => {
    if (!trackToDelete) return;

    setIsDeletingTrack(true);
    setDeleteTrackError(null);

    const response = await api.deleteTrack(trackToDelete.id);

    if (response.error) {
      setDeleteTrackError(response.error);
      setIsDeletingTrack(false);
      return;
    }

    // Close modal and refresh tracks
    setTrackToDelete(null);
    setIsDeletingTrack(false);
    refetch();
  };

  const confidence = persona?.current_memory?.overall_confidence || 0;

  // SINGLE RETURN - modal is always at the same tree position to prevent remounting
  return (
    <>
      {/* Conditional content - loading, error, or main view */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-jelly-500"></div>
        </div>
      ) : error || !persona ? (
        <div className="text-center py-12">
          <p className="text-red-400">{error || 'Persona not found'}</p>
          <Link to="/admin/personas" className="text-jelly-400 hover:text-jelly-300 mt-4 inline-block">
            Back to personas
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
      {/* Back link */}
      <Link to="/admin/personas" className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200">
        <ArrowLeft className="w-4 h-4" />
        Back to personas
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="relative group">
            {persona.avatar_url ? (
              <img
                src={persona.avatar_url}
                alt={persona.artist_name}
                className="w-20 h-20 rounded-full object-cover border-2 border-jelly-500"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-jelly-500 to-purple-600 flex items-center justify-center text-3xl font-bold">
                {persona.artist_name[0]}
              </div>
            )}
            <button
              onClick={handleGenerateAvatar}
              disabled={isGeneratingAvatar}
              className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              title={persona.avatar_url ? "Regenerate avatar" : "Generate avatar"}
            >
              {isGeneratingAvatar ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Sparkles className="w-6 h-6" />
              )}
            </button>
          </div>
          <div>
            <h1 className="text-2xl font-bold">{persona.artist_name}</h1>
            {persona.stage_name && <p className="text-zinc-400">{persona.stage_name}</p>}
            <div className="flex items-center gap-3 mt-2 text-sm text-zinc-500">
              <span className="capitalize">{persona.creation_method} creation</span>
              <span>•</span>
              <span>{persona.subscription_tier} tier</span>
              {avatarError && (
                <>
                  <span>•</span>
                  <span className="text-red-400">{avatarError}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Confidence */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Persona Confidence</h3>
            <p className="text-sm text-zinc-400">How well the AI understands this artist</p>
          </div>
          <span className="text-2xl font-bold">{Math.round(confidence * 100)}%</span>
        </div>
        <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-jelly-500 to-purple-500 transition-all"
            style={{ width: `${confidence * 100}%` }}
          />
        </div>
        <p className="text-sm text-zinc-500 mt-2">
          {confidence < 0.3
            ? 'Upload more tracks to improve understanding'
            : confidence < 0.6
            ? 'Good start! Add more content to refine'
            : confidence < 0.8
            ? 'Strong understanding of this artist'
            : 'Excellent! Deep understanding achieved'}
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tracks */}
        <Card>
          <CardHeader
            title="Tracks"
            description={`${tracks.length} uploaded`}
            action={
              <Button size="sm" variant="ghost" onClick={() => setIsUploadModalOpen(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            }
          />
          {tracks.length === 0 ? (
            <div className="text-center py-6">
              <Music className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">No tracks yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tracks.map((track) => {
                const status = getTrackStatus(track);
                return (
                  <div
                    key={track.id}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-zinc-800 transition-colors group"
                  >
                    <button
                      onClick={() => setSelectedTrack(track)}
                      className="flex-1 flex items-center gap-3 text-left"
                    >
                      <Music className="w-5 h-5 text-zinc-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{getTrackDisplayName(track)}</p>
                        <p className={`text-xs ${status.color} flex items-center gap-1`}>
                          {status.isAnalyzing && <Loader2 className="w-3 h-3 animate-spin" />}
                          {status.label}
                          {track.tempo_bpm ? ` • ${Math.round(track.tempo_bpm)} BPM` : ''}
                        </p>
                      </div>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Open VideoCreator with this track
                        setVideoCreatorTrack(track);
                      }}
                      disabled={!track.analyzed_at}
                      className="p-1.5 rounded-md hover:bg-zinc-700 transition-colors disabled:opacity-50"
                      title={track.analyzed_at ? "Create video" : "Track must be analyzed first"}
                    >
                      <Video className="w-4 h-4 text-zinc-400 hover:text-jelly-400" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTrackToDelete(track);
                      }}
                      className="p-1.5 rounded-md hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete track"
                    >
                      <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Images */}
        <Card>
          <CardHeader
            title="Images"
            description="0 uploaded"
            action={
              <Button size="sm" variant="ghost">
                <Plus className="w-4 h-4" />
              </Button>
            }
          />
          <div className="text-center py-6">
            <Image className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">No images yet</p>
          </div>
        </Card>

        {/* Projects */}
        <Card>
          <CardHeader
            title="Projects"
            description={`${videoProjects.length} video${videoProjects.length !== 1 ? 's' : ''}`}
          />
          {isLoadingVideos ? (
            <div className="text-center py-6">
              <Loader2 className="w-6 h-6 text-jelly-400 mx-auto mb-2 animate-spin" />
              <p className="text-zinc-400 text-sm">Loading videos...</p>
            </div>
          ) : videoProjects.length === 0 ? (
            <div className="text-center py-6">
              <Video className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">No videos yet</p>
              <p className="text-zinc-500 text-xs mt-1">Upload a track and create your first video</p>
            </div>
          ) : (
            <div className="space-y-3">
              {videoProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  {/* Thumbnail or placeholder */}
                  <div className="w-16 h-16 bg-zinc-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {project.output_url ? (
                      <video
                        src={project.output_url}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                    ) : (
                      <Video className="w-6 h-6 text-zinc-500" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{project.target_duration}s {project.target_platform}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        project.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                        project.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        project.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      {project.quality_tier} • {new Date(project.created_at).toLocaleDateString()}
                      {project.actual_cost_usd ? ` • $${project.actual_cost_usd.toFixed(2)}` : ''}
                    </p>
                  </div>

                  {/* Actions */}
                  {project.output_url && (
                    <a
                      href={project.output_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-jelly-500 hover:bg-jelly-600 text-white text-xs rounded-lg transition-colors"
                    >
                      Watch
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Memory preview */}
      {persona.current_memory && (
        <Card>
          <CardHeader title="AI Memory" description="What the AI has learned about this artist" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-zinc-500 mb-1">Genres</p>
              <p className="text-sm">
                {persona.current_memory.memory_data.identity.primary_genres.join(', ') || 'None detected'}
              </p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Themes</p>
              <p className="text-sm">
                {persona.current_memory.memory_data.identity.core_themes.join(', ') || 'None detected'}
              </p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Tracks Analyzed</p>
              <p className="text-sm">{persona.current_memory.tracks_analyzed}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Learning Events</p>
              <p className="text-sm">{persona.current_memory.total_learning_events}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !isDeleting && setIsDeleteModalOpen(false)} />
          <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/20 mx-auto mb-4">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Delete Persona</h3>
              <p className="text-zinc-400 text-center text-sm mb-4">
                Are you sure you want to delete <span className="text-white font-medium">{persona.artist_name}</span>?
                This will permanently delete all tracks, images, projects, and AI memory associated with this persona.
              </p>
              <p className="text-red-400 text-center text-xs mb-4">
                This action cannot be undone.
              </p>
              {deleteError && (
                <p className="text-red-400 text-center text-sm mb-4 p-2 bg-red-500/10 rounded-lg">
                  {deleteError}
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Persona'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Track Detail Modal */}
      {selectedTrack && (
        <TrackDetailModal
          isOpen={!!selectedTrack}
          onClose={() => setSelectedTrack(null)}
          track={selectedTrack}
          personaId={id!}
          onUploadAnother={() => setIsUploadModalOpen(true)}
        />
      )}

      {/* VideoCreator Modal - unified entry point for video creation */}
      {videoCreatorTrack && (
        <VideoCreator
          isOpen={!!videoCreatorTrack}
          onClose={() => setVideoCreatorTrack(null)}
          track={videoCreatorTrack}
          personaId={id!}
          onComplete={(videoUrl) => {
            console.log('Video created:', videoUrl);
            setVideoCreatorTrack(null);
            refetch();
          }}
        />
      )}

      {/* Delete Track Confirmation Modal */}
      {trackToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !isDeletingTrack && setTrackToDelete(null)} />
          <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/20 mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Delete Track</h3>
              <p className="text-zinc-400 text-center text-sm mb-4">
                Are you sure you want to delete <span className="text-white font-medium">{getTrackDisplayName(trackToDelete)}</span>?
                This will remove the track and its analysis data.
              </p>
              {deleteTrackError && (
                <p className="text-red-400 text-center text-sm mb-4 p-2 bg-red-500/10 rounded-lg">
                  {deleteTrackError}
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setTrackToDelete(null)}
                  disabled={isDeletingTrack}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  onClick={handleDeleteTrack}
                  disabled={isDeletingTrack}
                >
                  {isDeletingTrack ? 'Deleting...' : 'Delete Track'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      )}

      {/* TrackUploadModal - ALWAYS at same tree position to prevent remounting */}
      <TrackUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        personaId={id!}
        onUploadComplete={refetch}
      />
    </>
  );
}
